/**
 * usePlanFeature Hook
 * Checks a plan-tied boolean entitlement based on tenant.features.
 * Sibling to useModuleAccess (role-based RBAC) — this one is plan-based.
 */

import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { TenantFeatures } from '@/store/slices/authSlice';
import { useAuth } from './useAuth';

export const FEATURE_DISPLAY_NAMES: Record<keyof TenantFeatures, string> = {
  whatsappEnabled: 'WhatsApp',
  instagramEnabled: 'Instagram',
  llmConfigEnabled: 'Custom LLM Configuration',
  customBranding: 'Custom Branding',
  prioritySupport: 'Priority Support',
  sslEnabled: 'Custom SSL',
  apiAccess: 'External API Access',
  webhooksEnabled: 'Webhooks',
  campaignsEnabled: 'Campaigns',
  workflowsEnabled: 'Automation Workflows',
  enableRag: 'AI Knowledge Base (RAG)',
  aiToolCalling: 'AI Actions',
  httpAutomationEnabled: 'HTTP API Automation',
};

export const usePlanFeature = () => {
  const { user, tenant } = useAuth();
  const authState = useSelector((state: RootState) => state.auth);

  // Prefer from hook, fallback to Redux state — same pattern as useModuleAccess.
  const features = tenant?.features || authState.tenant?.features;

  const hasFeature = (key: keyof TenantFeatures): boolean => {
    // Superadmin has full access, same convention as useModuleAccess.
    if (user?.role === 'superadmin') return true;
    if (!features) return false;
    return features[key] === true;
  };

  return { features, hasFeature };
};
